import React from 'react';
import { StaticPageLayout } from '@/components/StaticPageLayout';
import { ExternalLink, Mail, Plus } from 'lucide-react';

type FeeWaiverPartner = {
  id: number;
  name: string;
  details: string | null;
  requirements: string | null;
  code: string | null;
  link: string | null;
};

type Props = {
  partners: FeeWaiverPartner[];
};

export default function FeeWaivers({ partners }: Props) {
  return (
    <StaticPageLayout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Fee Waiver Partners
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Project SHORT has partnered with leading universities to provide application 
              fee waivers for qualifying students, making higher education more accessible.
            </p>
          </div>
        </div>
      </section>

      {/* Partners List */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Partner Universities
            </h2>
            <p className="text-gray-600">
              The following universities offer fee waivers to Project SHORT mentees. 
              Each institution has specific requirements and processes.
            </p>
          </div>

          <div className="space-y-6">
            {partners.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-2xl">
                <p className="text-gray-600">
                  Fee waiver partnerships are being established. Please check back soon!
                </p>
              </div>
            ) : (
              partners.map((partner) => (
              <div
                key={partner.id}
                className="bg-gray-50 rounded-2xl p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {partner.name}
                    </h3>
                    {partner.details && (
                      <p className="text-gray-600 mb-2">{partner.details}</p>
                    )}
                    {partner.code && (
                      <p className="text-sm text-primary font-medium">
                        Fee waiver code: {partner.code}
                      </p>
                    )}
                    {partner.requirements && (
                      <p className="text-sm text-gray-600 mt-1">
                        {partner.requirements}
                      </p>
                    )}
                  </div>
                  {partner.link && (
                    <a
                      href={partner.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 p-2 bg-white rounded-lg hover:bg-primary/10 transition-colors"
                    >
                      <ExternalLink className="h-5 w-5 text-primary" />
                    </a>
                  )}
                </div>
              </div>
            )))
          }</div>

          {/* Important Notice */}
          <div className="mt-12 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Important Information
            </h3>
            <ul className="text-gray-700 space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>International students are generally not eligible for fee waivers</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Fee waiver eligibility and requirements may change - please verify with each institution</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Contact your Project SHORT mentor for guidance on fee waiver applications</span>
              </li>
            </ul>
          </div>

          {/* Contact Section */}
          <div className="mt-12 text-center bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Have Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              For inquiries about fee waivers or to request adding a graduate school to our program:
            </p>
            <a
              href="mailto:contact@project-short.com"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary/90 transition-colors"
            >
              <Mail className="h-5 w-5 mr-2" />
              Contact Us
            </a>
          </div>

          {/* Request New Partner */}
          <div className="mt-12 border-t pt-12">
            <div className="text-center">
              <Plus className="h-8 w-8 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Don't see your school?
              </h3>
              <p className="text-gray-600 mb-4">
                We're always working to expand our fee waiver partnerships. 
                Let us know which schools you'd like us to partner with.
              </p>
              <a
                href="mailto:contact@project-short.com?subject=Fee%20Waiver%20Partner%20Request"
                className="text-primary hover:text-primary/80 font-medium"
              >
                Request a new partnership →
              </a>
            </div>
          </div>
        </div>
      </section>
    </StaticPageLayout>
  );
}